import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Waves, Thermometer, Activity, LifeBuoy, Ruler, Sun } from "lucide-react";

export function useHooch() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/hooch").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 900_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const TONE = {
  good: "#16a34a",
  watch: "#f59e0b",
  bad: "#dc2626",
  none: "#64748b",
};

const TREND_LABEL = { rising: "▲ Rising", falling: "▼ Falling", steady: "● Steady" };

function Stat({ icon: Icon, label, value, unit, sub, color = "#2a74d8" }) {
  return (
    <div className="flex flex-col justify-center rounded-[6px] bg-white px-6 py-4 shadow-[0_12px_30px_rgba(10,15,28,0.14)]" style={{ borderLeft: `10px solid ${color}` }}>
      <div className="flex items-center gap-2 font-oswald text-[19px] font-bold uppercase tracking-[0.22em] text-[#5b6675]">
        <Icon className="h-5 w-5" style={{ color }} /> {label}
      </div>
      <div className="mt-1 font-oswald text-[58px] font-black leading-[0.9] text-[#0a0f1c]">
        {value}
        {unit ? <span className="ml-1 text-[24px] font-bold text-[#5b6675]">{unit}</span> : null}
      </div>
      {sub ? <div className="font-oswald text-[20px] font-semibold uppercase tracking-wide text-[#5b6675]">{sub}</div> : null}
    </div>
  );
}

export function HoochSlide({ hooch, cities }) {
  if (!hooch || hooch.flow_cfs == null) return null;
  const v = hooch.verdict || {};
  const tone = TONE[v.tone] || TONE.none;
  const atl = (cities || []).find((c) => c.id === "atlanta");
  const airTemp = atl?.current_temp;

  return (
    <div data-testid="hooch-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #06283d 0%, #0b4a6f 55%, #1b6ca8 100%)" }}>
      {/* faint flowing-water texture */}
      <div className="absolute inset-0 opacity-[0.10]" style={{ backgroundImage: "repeating-linear-gradient(115deg, #ffffff 0 2px, transparent 2px 30px)" }} />

      <div className="relative grid h-full w-full grid-cols-[42%_1fr] gap-6 px-[48px] py-[22px]">
        {/* Verdict + flow hero */}
        <div className="flex flex-col justify-center rounded-[8px] bg-white px-8 py-7 shadow-[0_18px_44px_rgba(0,0,0,0.3)]" style={{ borderLeft: `14px solid ${tone}` }}>
          <div className="flex items-center gap-2 font-oswald text-[21px] font-bold uppercase tracking-[0.3em] text-[#5b6675]">
            <Waves className="h-6 w-6" style={{ color: tone }} /> Shoot the Hooch · Float Report
          </div>
          <div className="mt-2 font-oswald text-[72px] font-black uppercase leading-[0.9] tracking-wide" style={{ color: tone }} data-testid="hooch-verdict">
            {v.label}
          </div>
          <div className="mt-4 flex items-end gap-3">
            <div className="font-oswald text-[132px] font-black leading-[0.8] text-[#0a0f1c]" data-testid="hooch-flow">{hooch.flow_cfs.toLocaleString()}</div>
            <div className="pb-4 font-oswald text-[26px] font-bold uppercase text-[#5b6675]">cfs<br /><span className="text-[16px] tracking-wider">river flow</span></div>
          </div>
          {hooch.trend ? (
            <div className="mt-1 font-oswald text-[20px] font-bold uppercase tracking-widest" style={{ color: tone }}>{TREND_LABEL[hooch.trend]}</div>
          ) : null}
          <div className="mt-5 font-oswald text-[26px] font-semibold leading-snug text-[#2c3542]">{v.note}</div>
        </div>

        {/* Conditions grid */}
        <div className="grid grid-cols-2 grid-rows-2 gap-4">
          <Stat icon={Thermometer} label="Water Temp" value={hooch.water_temp_f ?? "—"} unit={hooch.water_temp_f != null ? "°F" : ""} sub={hooch.cold_note ? "Brrr · cold dam release" : "Comfortable"} color="#0ea5e9" />
          <Stat icon={Sun} label="Air · Atlanta" value={airTemp != null ? Math.round(airTemp) : "—"} unit={airTemp != null ? "°F" : ""} sub={atl?.condition || "—"} color="#f59e0b" />
          <Stat icon={Ruler} label="Gauge Height" value={hooch.gauge_height_ft ?? "—"} unit={hooch.gauge_height_ft != null ? "ft" : ""} sub="Stage" color="#2a74d8" />
          <div className="flex flex-col justify-center rounded-[6px] px-6 py-4 text-white shadow-[0_12px_30px_rgba(10,15,28,0.2)]" style={{ background: tone }}>
            <div className="flex items-center gap-2 font-oswald text-[19px] font-bold uppercase tracking-[0.22em] text-white/90">
              <LifeBuoy className="h-5 w-5" /> Safety
            </div>
            <div className="mt-1 font-oswald text-[27px] font-black uppercase leading-tight">
              {v.tone === "bad" ? "Stay off the river" : v.tone === "watch" ? "Wear a life jacket" : "Always wear a PFD"}
            </div>
            <div className="mt-1 font-oswald text-[19px] font-semibold uppercase tracking-wide text-white/85">Johnson Ferry → Powers Island</div>
          </div>
        </div>
      </div>

      {/* source footer */}
      <div className="absolute bottom-[10px] left-[48px] flex items-center gap-2 font-oswald text-[18px] font-semibold uppercase tracking-[0.18em] text-white/70">
        <Activity className="h-4 w-4" /> {hooch.source}
      </div>
    </div>
  );
}
